import {reactive} from 'vue'
import {openDB, deleteDB} from 'idb'
import {api} from '@/services/api.js'
import {newCorrelationId} from '@/utils/correlation.js'

/**
 * The scan queue: a scan that cannot reach the api is kept on the handset and
 * replayed later under the same idempotency key, so the server redeems it once
 * (docs/adr/0001-scan-idempotency-table.md). The only offline queue in the PWA;
 * the service worker passes scans straight through.
 *
 * A queued scan leaves the queue when the server answers it: accepted, refused,
 * or a duplicate (answered with the first attempt's outcome). Network failures
 * and 5xx answers are retried up to MAX_REPLAY_ATTEMPTS, then given up; while
 * the handset is offline or the rep signed out, nothing is spent. A refusal, a
 * give-up or a duplicate with no known outcome becomes a notice so the rep can
 * check that guest by hand.
 */
export const MAX_REPLAY_ATTEMPTS = 10

// Records queued by the pre-v3 event screen, replayed on v3 under their key.
const LEGACY_ENDPOINTS = {
    '/api/events/validate-in': '/api/v3/scan/event-in',
    '/api/events/validate-out': '/api/v3/scan/event-out',
}

const toQueuedScan = (record) => {
    if (record.endpoint) return record
    const pathname = new URL(record.url).pathname
    return {
        id: record.id,
        endpoint: LEGACY_ENDPOINTS[pathname] || pathname,
        payload: record.data,
        label: String(record.data?.barcode ?? ''),
        correlationId: record.options?.correlationId,
        attempts: record.retryCount || 0,
    }
}

// A request that never got an answer, or one the server could not process.
const isRetryable = (error) => !error.status || error.status >= 500 || error.status === 408 || error.status === 429

/**
 * What a failed send means for the scan: wait (signed out, or offline), retry,
 * accepted, refused, or unknown.
 */
function answerOutcome(error, isOnline) {
    if (error.code === 'auth_required' || !isOnline()) return {outcome: 'wait'}
    if (isRetryable(error)) return {outcome: 'retry'}
    if (error.code !== 'duplicate') return {outcome: 'refused', error}
    // The key was used before: answer with the first attempt's outcome. A key
    // found only in the scan log (pruned, or used by the pre-v3 routes) has
    // none, and that attempt may have been refused (docs/adr/0001).
    const original = error.original
    if (!original) return {outcome: 'unknown'}
    if (original.outcome === 'pending') return {outcome: 'retry'}
    if (original.outcome === 'refused') {
        return {outcome: 'refused', error: Object.assign(new Error(original.error), {code: original.code, status: original.status})}
    }
    // accepted, or a redeem with its own outcome (a transfer loaded_as_extra).
    return {outcome: 'accepted'}
}

/**
 * @param {object} deps
 * @param {{add: Function, all: Function, put: Function, delete: Function}} deps.store
 * @param {(endpoint: string, payload: object, options: object) => Promise<object>} deps.send
 * @param {() => boolean} [deps.isOnline]
 * @param {() => number} [deps.now] ms
 * @param {number} [deps.maxAttempts]
 */
export function createScanQueue({store, send, isOnline = () => navigator.onLine, now = Date.now, maxAttempts = MAX_REPLAY_ATTEMPTS}) {
    const status = reactive({count: 0, syncing: false, notices: []})

    const refreshCount = async () => {
        try {
            status.count = (await store.all()).length
        } catch (error) {
            console.error('Failed to count queued scans:', error)
        }
        return status.count
    }

    const enqueue = async (scan) => {
        await store.add({...scan, attempts: 0, queuedAt: now()})
        await refreshCount()
        return {queued: true}
    }

    /**
     * Sends a scan, or queues it when the handset is offline or the request
     * gets no answer. Resolves {queued: false, response} when accepted and
     * {queued: true} when queued; throws the refusal otherwise.
     * @param {string} endpoint
     * @param {object} payload
     * @param {string} label who the scan was for, shown if a replay is refused
     */
    async function submit(endpoint, payload, label) {
        // The scan time travels with the scan: the server checks QR expiry
        // against it, not against when the queue gets back online.
        const scan = {endpoint, payload: {...payload, clientTimestamp: payload.clientTimestamp ?? now()}, label, correlationId: newCorrelationId()}
        if (!isOnline()) return enqueue(scan)
        try {
            return {queued: false, response: await send(endpoint, scan.payload, {correlationId: scan.correlationId})}
        } catch (error) {
            const result = answerOutcome(error, isOnline)
            if (result.outcome === 'wait' && error.code === 'auth_required') throw error
            if (result.outcome === 'wait' || result.outcome === 'retry') return enqueue(scan)
            if (result.outcome === 'refused') throw result.error
            return {queued: false, response: {success: true, outcome: 'accepted', duplicate: true}}
        }
    }

    /** Replays every queued scan once. Returns what happened to each. */
    async function replay() {
        if (status.syncing || !isOnline()) return []
        status.syncing = true
        const results = []
        try {
            for (const record of await store.all()) {
                const scan = toQueuedScan(record)
                let result
                try {
                    await send(scan.endpoint, scan.payload, {correlationId: scan.correlationId})
                    result = {outcome: 'accepted'}
                } catch (error) {
                    result = answerOutcome(error, isOnline)
                }

                // Not the scan's fault: leave the rest for the next replay.
                if (result.outcome === 'wait') break

                if (result.outcome === 'retry') {
                    const attempts = scan.attempts + 1
                    if (attempts < maxAttempts) {
                        await store.put({...scan, attempts})
                        results.push({outcome: 'retry', label: scan.label})
                        continue
                    }
                    result = {outcome: 'gave_up'}
                }

                await store.delete(scan.id)
                if (result.outcome === 'refused') status.notices.push({outcome: 'refused', label: scan.label, message: result.error.message})
                if (result.outcome === 'gave_up' || result.outcome === 'unknown') status.notices.push({outcome: result.outcome, label: scan.label})
                results.push({outcome: result.outcome, label: scan.label})
            }
        } finally {
            await refreshCount()
            status.syncing = false
        }
        return results
    }

    const dismissNotice = (index) => status.notices.splice(index, 1)

    return {status, submit, replay, refreshCount, dismissNotice}
}

// The store the pre-v3 queue wrote to, so scans queued before the update are replayed.
const DB_NAME = 'offline-requests'
const STORE_NAME = 'requests'
const createStore = (db) => {
    if (!db.objectStoreNames.contains(STORE_NAME)) db.createObjectStore(STORE_NAME, {keyPath: 'id', autoIncrement: true})
}

let dbPromise = null
const getDB = () => {
    dbPromise ??= openDB(DB_NAME, 1, {upgrade: createStore}).catch(async (error) => {
        // A corrupted database or a missing object store: start again.
        if (error.name !== 'NotFoundError' && !error.message?.includes('object store')) {
            dbPromise = null
            throw error
        }
        console.warn('IndexedDB corrupted, recreating database...')
        await deleteDB(DB_NAME)
        return openDB(DB_NAME, 1, {upgrade: createStore})
    })
    return dbPromise
}

const idbStore = {
    add: async (record) => (await getDB()).add(STORE_NAME, record),
    all: async () => (await getDB()).getAll(STORE_NAME),
    put: async (record) => (await getDB()).put(STORE_NAME, record),
    delete: async (id) => (await getDB()).delete(STORE_NAME, id),
}

export const scanQueue = createScanQueue({
    store: idbStore,
    send: (endpoint, payload, options) => api.post(endpoint, payload, options),
})
